import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
  Pressable,
  Platform,
  StatusBar,
  Linking,
  Image,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import viewmapImg from '../../image/viewmap.png';

import { useNavigation, useRoute } from '@react-navigation/native';
import {
  callApi,
  formatDate,
  getUserIdEmailPhone,
  handleVerifyUser,
  performFollow,
} from '../Helper/Helper';
import mystyles from '../../assets/styles/mystyles';

// Reusable component to list offers based on itemId and userId
const OfferListView = ({ endpoint, userId, itemId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [followBusy, setFollowBusy] = useState({});
  const [userInfo, setUserInfo] = useState(null);
  const navigation = useNavigation();

  useEffect(() => {
    (async () => {
      try {
        const info = await getUserIdEmailPhone();
        setUserInfo(info);
      } catch (e) {
        console.warn('Failed to get user info', e);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchOffers = async () => {
      if (!userId || !itemId) return;
      setLoading(true);
      setError('');
      setData(null);

      try {
        // Construct endpoint with itemId and userId
        const url = `${endpoint}/${encodeURIComponent(userId)}/${encodeURIComponent(itemId)}`;
        const json = await callApi('GET', url);
        setData(json);
      } catch (e) {
        setError(e?.message || 'Something went wrong while fetching offers.');
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, [endpoint, userId, itemId]);

  const handleFollow = async (item, rowKey) => {
    const followItem = { userId: item.pUserId };
    await performFollow(
      followItem,
      userInfo?.userId,
      userInfo?.email,
      userInfo?.phone,
      setFollowBusy,
      rowKey
    );
  };

  if (loading) {
    return (
      <View style={mystyles.centered}>
        <ActivityIndicator size="large" color="#0A84FF" />
        <Text style={mystyles.loadingText}>Loading offers...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={mystyles.errorBox}>
        <Text style={mystyles.errorText}>{error}</Text>
      </View>
    );
  }

  const items = data?.Items || (Array.isArray(data) ? data : []);

  if (items.length === 0) {
    return (
      <View style={{ padding: 20, alignItems: 'center' }}>
        <Text style={{ color: '#666' }}>No offers found for this request.</Text>
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 100 }}>
      {items.map((item, idx) => {
        const rowKey = item?.id ?? item?.pk ?? String(idx);
        const itemDescription = item?.itemDescription;
        const offerDescription = item?.offerDescription;
        const price = item?.price;
        const phone = item?.offererPhoneNumber;
        const email = item?.offererEmail;
        const pUserId = item?.pUserId;

        return (
          <View key={rowKey} style={[mystyles.card, idx % 2 === 1 && mystyles.cardAlt]}>
            {!!itemDescription && (
              <View style={styles.highlightedTitleContainer}>
                <Text style={[mystyles.title, styles.highlightedTitle]}>
                  {String(itemDescription).replace(/\s*\n+\s*/g, ' ').trim()}
                </Text>
              </View>
            )}

            <Text style={styles.offerDetailsLabel}>Offer Details:</Text>

            {!!price && <Text style={styles.simplePrice}>Price: {price}</Text>}

            {!!offerDescription && <Text style={mystyles.description}>{offerDescription}</Text>}

            {!!pUserId && (
              <View style={styles.userContainer}>
                <Text style={styles.offeredByLabel}>Offered by:</Text>
                <Text style={styles.userIdText}>{pUserId}</Text>
              </View>
            )}

            <View style={styles.actionsRow}>
              {!!phone && (
                <>
                  <Pressable
                    onPress={() => Linking.openURL(`tel:${phone}`)}
                    style={({ pressed }) => [styles.actionPill, pressed && styles.actionPillPressed]}
                  >
                    <Text style={styles.actionIcon}>📞</Text>
                    <Text style={styles.actionText}>Call</Text>
                  </Pressable>

                  <Pressable
                    onPress={() => Linking.openURL(`sms:${phone}`)}
                    style={({ pressed }) => [styles.actionPill, pressed && styles.actionPillPressed]}
                  >
                    <Text style={styles.actionIcon}>💬</Text>
                    <Text style={styles.actionText}>Text</Text>
                  </Pressable>
                </>
              )}

              {!!pUserId && (
                <>
                  <Pressable
                    onPress={() => navigation.navigate('ProviderDetails', { pUserId })}
                    style={({ pressed }) => [styles.actionPill, pressed && styles.actionPillPressed]}
                  >
                    <Text style={styles.actionIcon}>😊</Text>
                    <Text style={styles.actionText}>Profile</Text>
                  </Pressable>

                  <Pressable
                    onPress={() => handleVerifyUser(pUserId)}
                    style={({ pressed }) => [styles.actionPill, pressed && styles.actionPillPressed]}
                  >
                    <Image
                      source={viewmapImg}
                      style={styles.mapIconSmall}
                    />
                    <Text style={styles.actionText}>Map</Text>
                  </Pressable>

                  <Pressable
                    onPress={() => handleFollow(item, rowKey)}
                    disabled={!!followBusy[rowKey]}
                    style={({ pressed }) => [
                      styles.followPill,
                      !!followBusy[rowKey] && styles.followPillDisabled,
                      pressed && !followBusy[rowKey] && styles.followPillPressed,
                    ]}
                  >
                    <Text style={styles.followText}>
                      {followBusy[rowKey] ? 'Following…' : 'Follow me'}
                    </Text>
                  </Pressable>
                </>
              )}
            </View>
          </View>
        );
      })}
    </ScrollView>
  );
};

export default function ItemOffers() {
  const route = useRoute();
  const { endpoint, userId, itemId } = route.params || {};

  return (
    <SafeAreaView style={mystyles.safe}>
      <StatusBar barStyle="light-content" backgroundColor="#0A84FF" />
      <View style={styles.headerContainer}>
        <Text style={styles.headerTitle}>Item Offers</Text>
      </View>
      <View style={mystyles.content}>
        {userId && itemId ? (
          <OfferListView endpoint={endpoint} userId={userId} itemId={itemId} />
        ) : (
          <View style={mystyles.centered}>
            <Text>Missing parameters to load offers.</Text>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    padding: 15,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  highlightedTitleContainer: {
    backgroundColor: '#F0F8FF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    alignItems: 'center',
  },
  highlightedTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#007AFF',
    textAlign: 'left',
    alignSelf: 'stretch',
  },
  offerDetailsLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 5,
    color: '#333',
  },
  simplePrice: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#27AE60',
    marginBottom: 6,
  },
  userContainer: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  offeredByLabel: {
    fontSize: 12,
    color: '#666',
    marginRight: 4,
  },
  userIdText: {
    fontSize: 14,
    color: '#1A5FB4',
    fontWeight: '600',
  },
  actionsRow: {
    marginTop: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 10,
  },
  actionPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  actionPillPressed: { opacity: 0.75 },
  actionIcon: { fontSize: 16 },
  actionText: { fontSize: 12, fontWeight: '700', color: '#0F172A' },
  mapIconSmall: { width: 18, height: 18, resizeMode: 'contain' },
  followPill: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
    backgroundColor: '#0A84FF',
  },
  followPillPressed: { opacity: 0.85 },
  followPillDisabled: { opacity: 0.55 },
  followText: { fontSize: 12, fontWeight: '800', color: '#FFFFFF' },
});
